import { useEffect, useRef, useState } from 'react'

import { ClockCircleOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, UpOutlined } from "@ant-design/icons"
import classNames from "classnames"
import _ from 'lodash'
import moment from "moment"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { AI_BOT_USER_ID } from "shared/constants/ai"
import { Button, Dropdown, Tooltip } from 'shared/ui'
import { getAttachmentPreviewLink } from "shared/utils/filesUtils"
import { formatTime } from "shared/utils/time"
import styled, { createGlobalStyle } from "styled-components"
import { useTranslation } from "react-i18next"
import { useIsOverflow } from 'shared/hooks/useIsOverflow'

import { useAppId } from '../../../shared/hooks'
import { getFilesPreviewIsPublicLinkSelector, getFilesPreviewSourceCommentIdSelector } from '../model/selectors/filesPreviewSelectors'
import { addAnimatedCommentId, filesPreviewActions, resetAIMessageAnimationFlag } from '../model/slice/filesPreviewSlice'

import { FormattedMessage } from './FormattedMessage'
import { MessageWriter } from "./MessageWriter"
import { StatusButton, StatusButtonType } from './components/StatusButton'

export interface CommentType {
  id: number
  message: string
  author_name: string
  author_id: string
  created: string
  status?: 'open' | 'resolved'
  avatar?: string
  replies?: CommentType[]
  annotation?: {
    type: string
    coordinates: number[]
    offset?: number
  }
  isNewAIMessage?: boolean
  _embedded?: {
    attachments?: ICommentAttachment[]
  }
}

export interface ICommentAttachment {
  id: string;
  type: string;
  _embedded: {
    previews: {
      sizes: {
        album: {
          type: string;
        };
      };
      url_template: string
    };
  };
}

interface ICommentProps {
  avatarUrl: string;
  onEdit: (id: string, data: any) => void;
  onDelete: (id: string) => void;
  onReply?: (id: string, data: any) => void;
  onClickReply?: (id: string) => void;
  onStatusChange?: (id: string, status: 'open' | 'resolved') => void;
  message: string;
  author_name: string;
  author_id: string;
  created: string;
  status?: 'open' | 'resolved';
  replies?: CommentType[];
  id?: string;
  currentUserName: string;
  currentUserId: string;
  currentUserAvatar?: string;
  videoTimestamp?: number;
  onCommentClick?: (timestamp: number) => void;
  isActive?: boolean;
  isRepliesVisibleByDefault?: boolean;
  onMount?: (commentId: string) => void;
  replyingCommentId?: string | null;
  hideReplyBtn?: boolean;
  onAnyFocusChange?: (focused: boolean) => void;
  isNewAIMessage?: boolean;
  isEditEnabled?: boolean;
  className?: string;
  attachments?: ICommentAttachment[];
  onAttachmentClick?: (attachment: ICommentAttachment, commentId: string) => void;
  annotation?: {
    type: string
    coordinates: number[]
    offset?: number
  };
  replyToId?: string;
}

const GlobalStyleForTooltip = createGlobalStyle`
.ant-tooltip {
  margin-right: -22px!important;
}
`

export const Comment: React.FC<ICommentProps> = ({
  id,
  avatarUrl,
  author_name,
  author_id,
  message,
  created,
  status = 'open',
  replies,
  onEdit,
  onDelete,
  onReply,
  onClickReply,
  onStatusChange,
  onCommentClick,
  currentUserName,
  currentUserId,
  currentUserAvatar,
  videoTimestamp,
  isActive,
  isRepliesVisibleByDefault = false,
  onMount,
  replyingCommentId,
  hideReplyBtn,
  onAnyFocusChange = _.noop,
  isNewAIMessage = false,
  isEditEnabled = true,
  className,
  attachments,
  onAttachmentClick,
  annotation,
  replyToId,
}) => {
  const { t }  = useTranslation()
  const [isRepliesVisible, setIsRepliesVisible] = useState(isRepliesVisibleByDefault)
  const [isReplyFormVisible, setIsReplyFormVisible] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState(message)
  const [editCommentFocused, setEditCommentFocused] = useState(false)
  const [replyingCommentFocused, setReplyingCommentFocused] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)


  const actionRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useAppDispatch()
  const sourceCommentId = useAppSelector(getFilesPreviewSourceCommentIdSelector)
  const animatedCommentIds = useAppSelector(state => state.filesPreview.animatedCommentIds)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const { isAivard } = useAppId()

  const aiBotAvatarSrc = isAivard ? '/icons/aivard-avatar.svg' : '/icons/yagel/yagel-avatar.svg'

  const hasReplies = replies && replies.length > 0
  const isHighlighted = sourceCommentId === String(id)
  const commentId = id

  const shouldAnimate = isNewAIMessage && !animatedCommentIds.includes(commentId)
  const anonymousUserName = t('l_comments_userWithoutName')
  const anonymousUserShortName = anonymousUserName && anonymousUserName[0] || ''

  const hasAnnotation = Boolean(
    annotation?.coordinates?.length >= 2 &&
    annotation.coordinates[0] !== -1 &&
    annotation.coordinates[1] !== -1
  )

  const { ref: authorRef, isOverflow, recompute } = useIsOverflow([author_name])

  useEffect(() => {
    if(!isReplyFormVisible && replyingCommentId) {
      dispatch(filesPreviewActions.setReplyingCommentId(null))
    }
  }, [isReplyFormVisible])

  useEffect(() => {
    setIsReplyFormVisible(id === replyingCommentId)
  }, [replyingCommentId, id])

  useEffect(() => {
    if (replies && replies.length > 0) {
      const hasAIReply = replies.some(reply => reply.author_id === String(AI_BOT_USER_ID))
      if (hasAIReply && !isRepliesVisible) {
        setIsRepliesVisible(true)
      }
    }
  }, [replies, isRepliesVisible])

  useEffect(() => {
    onAnyFocusChange(editCommentFocused || replyingCommentFocused)
  }, [editCommentFocused, replyingCommentFocused])

  const handleCommentEdit = () => {
    if (status === 'resolved') {
      return
    }
    setIsEditing(true)
    setEditedContent(message)
  }

  const handleEditedCommentSend = (data: any) => {
    onEdit(id, data.message)
    setIsEditing(false)
  }

  const handleCommentDelete = () => {
    onDelete(id)
  }

  const handleStatusChange = (newStatus: 'open' | 'resolved') => {
    onStatusChange?.(id, newStatus)
  }

  const handleReplySend = (data: { message: string, mentions: any }) => {
    onReply(id, data)
    setIsReplyFormVisible(false)
    setIsEditing(false)

    setIsRepliesVisible(true)
  }

  const commentMenuItems = [
    ...(status !== 'resolved' ? [{
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: handleCommentEdit,
    }] : []),
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleCommentDelete,
    },
  ]

  const handleEditCommentFocusChange = (focused: boolean) => {
    setEditCommentFocused(focused)
  }

  const handleEditReplyFocusChange = (focused) => {
    setReplyingCommentFocused(focused)
  }

  useEffect(() => {
    if (onMount) {
      onMount(id.toString())
    }
  }, [id])

  useEffect(() => {
    if (isEditing || isReplyFormVisible) {
      actionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isEditing, isReplyFormVisible])

  const handleClickReplay = (e, commentId) => {
    e.preventDefault()
    e.stopPropagation()
    onClickReply(commentId)
  }

  const handleAnimationComplete = (animatedCommentId: string) => {
    dispatch(addAnimatedCommentId({ commentId: animatedCommentId }))
    dispatch(resetAIMessageAnimationFlag({ commentId: animatedCommentId }))
  }

  return (
    <SComment
      id={String(id)}
      onClick={() => onCommentClick?.(videoTimestamp)}
      className={classNames(className, { highlighted: isHighlighted })}
      active={isActive || false}
      data-timestamp={videoTimestamp}
      data-comment-id={id}
    >
      <GlobalStyleForTooltip />

      {isEditing ? (
        <SEditRow
          ref={actionRef}
          onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsEditing(false)
            }
          }}
        >
          {
            String(author_id) === String(AI_BOT_USER_ID) ? (
              <SAvatarLogo src={aiBotAvatarSrc} />
            ) : avatarUrl ? (
              <SAvatarImage src={avatarUrl}
                alt={author_name}
              />
            ) : currentUserAvatar ? (
              <SAvatarImage src={currentUserAvatar}
                alt={author_name}
              />
            ) : (
              <SAvatar>
                {author_name[0]}
              </SAvatar>
            )
          }

          <SMessageWriter
            type="edit"
            className="edit-comment-writer"
            onClickOutside={() => setIsEditing(false)}
            onSend={handleEditedCommentSend}
            defaultValue={editedContent}
            menuPosition="bottom"
            onFocusChange={handleEditCommentFocusChange}
            initialFocus={true}
          />
        </SEditRow>
      ) : (
        <SCommentContent className={classNames(className, { highlighted: isHighlighted, active: isActive })}>
          <SActionButtons>
            {author_id === currentUserId && isEditEnabled && !replyToId && (
              <Dropdown
                menu={{ items: commentMenuItems }}
                placement="bottomLeft"
                trigger={['hover']}
                getPopupContainer={triggerNode => triggerNode.parentElement}
                onOpenChange={(visible) => setIsDropdownOpen(visible)}
              >
                <SMenuButton
                  type="text"
                  icon={<EllipsisOutlined />}
                  $active={isDropdownOpen}
                />
              </Dropdown>
            )}

            {/* TODO: убрать флаг isPublicLink после доработок на странице публичного линков */}
            {!replyToId && onStatusChange && !isPublicLink && (
              <StatusButton
                commentType={StatusButtonType.DEFAULT}
                status={status}
                onStatusChange={() => handleStatusChange(status === 'open' ? 'resolved' : 'open')}
                showTooltip={true}
              />
            )}
          </SActionButtons>

          <SCommentHeader>
            {
              String(author_id) === String(AI_BOT_USER_ID) ? (
                <SAvatarLogo src={aiBotAvatarSrc} />
              ) : avatarUrl ? (
                <SAvatarImage
                  src={avatarUrl}
                  alt={author_name}
                />
              ) : (
                <SAvatar>
                  {author_name[0] || anonymousUserShortName}
                </SAvatar>
              )
            }

            <SAuthorInfo>
              <Tooltip
                title={isOverflow ? (author_name || anonymousUserName) : undefined}
                placement="top"
                destroyTooltipOnHide
              >
                <SAuthorName
                  ref={authorRef}
                  onMouseEnter={recompute}
                >
                  {author_name || anonymousUserName}
                </SAuthorName>
              </Tooltip>

              <STimestamp>
                {moment(created).fromNow()}
              </STimestamp>
            </SAuthorInfo>
          </SCommentHeader>

          <SContent>
            <SAnnotationLabelContainer>
              {typeof videoTimestamp === 'number' && videoTimestamp > 0 && (
                <SCommentTime>
                  <SCommentTimeIcon />

                  {formatTime(videoTimestamp)}
                </SCommentTime>
              )}

              {hasAnnotation && (
                <SAnnotationLabel>
                  <AnnotateSvgIcon fill="rgba(255, 255, 255, 0.85)" />

                  {'Annotate'}
                </SAnnotationLabel>
              )}
            </SAnnotationLabelContainer>

            <FormattedMessage
              message={message}
              needToParseFromMarkdown={String(author_id) === String(AI_BOT_USER_ID)}
              isAIMessage={String(author_id) === String(AI_BOT_USER_ID)}
              shouldAnimate={String(author_id) === String(AI_BOT_USER_ID) && shouldAnimate}
              commentId={commentId}
              onAnimationComplete={handleAnimationComplete}
            />

            {attachments && attachments.length > 0 && (
              <SAttachmentsContainer>
                {attachments.map((attachment) => (
                  <SAttachmentImage
                    key={attachment.id}
                    src={getAttachmentPreviewLink(attachment)}
                    onClick={() => onAttachmentClick?.(attachment, String(id))}
                    alt="Attachment"
                    className={classNames({ active: isHighlighted })}
                  />
                ))}
              </SAttachmentsContainer>
            )}
          </SContent>

          {hasReplies && (
            <>
              <SRepliesToggle onClick={() => setIsRepliesVisible(!isRepliesVisible)}>
                {replies!.length}

                {replies!.length === 1 ? ' Reply' : ' Replies'}

                <SToggleIcon isVisible={isRepliesVisible}>
                  <UpOutlined />
                </SToggleIcon>
              </SRepliesToggle>
            </>
          )}

          {
            !replyingCommentId && !!onReply && !isEditing && !hideReplyBtn && isEditEnabled &&
            (!hasReplies || !!replyToId) && (
              <SReplyBtnRow>
                <SReplyBtn onClick={(e) => handleClickReplay(e, replyToId || id)}>
                  Reply to
                </SReplyBtn>
              </SReplyBtnRow>
            )
          }

        </SCommentContent>
      )}


      {isRepliesVisible && replies && replies.length > 0 && (
        <SRepliesContainer>
          {replies.map((reply, index) => {
            const isLastReply = index === replies.length - 1

            return (
              <SReplyWrapper key={reply.id}>
                <Comment
                  {...reply}
                  id={String(reply.id)}
                  author_name={reply.author_name}
                  author_id={reply.author_id}
                  avatarUrl={reply.avatarUrl || ''}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  currentUserName={currentUserName}
                  currentUserId={currentUserId}
                  currentUserAvatar={currentUserAvatar}
                  onCommentClick={onCommentClick}
                  onMount={onMount}
                  isNewAIMessage={reply.isNewAIMessage}
                  attachments={reply._embedded?.attachments}
                  onAttachmentClick={onAttachmentClick}
                  onReply={onReply}
                  onClickReply={onClickReply}
                  hideReplyBtn={hideReplyBtn || !isLastReply}
                  replyToId={isLastReply ? String(id) : undefined}
                  replyingCommentId={replyingCommentId}
                />
              </SReplyWrapper>
            )
          })}
        </SRepliesContainer>
      )}


      {isReplyFormVisible  && (
        <SReplyFormRow
          ref={actionRef}
          onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsReplyFormVisible(false)
            }
          }}
        >
          {currentUserAvatar ? (
            <SAvatarImage src={currentUserAvatar}
              alt={currentUserName}
            />
          ) : (
            <SAvatar>
              {currentUserName?.[0] || anonymousUserShortName}
            </SAvatar>
          )}

          <MessageWriter
            type="new"
            initialFocus={id === replyingCommentId}
            onClickOutside={() => setIsReplyFormVisible(false)}
            onSend={handleReplySend}
            menuPosition="bottom"
            onFocusChange={handleEditReplyFocusChange}
          />
        </SReplyFormRow>
      )}
    </SComment>
  )
}


const SMenuButton = styled(Button)<{ $active?: boolean }>`
  && {
    display: none!important;
    width: 25px !important;
    height: 25px !important;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background-color: ${({ $active }) => ($active ? '#424242' : 'transparent')};
    border-radius: 4px;
    font-size: 16px !important;
    pointer-events: none;
  }
`

const SActionButtons = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  z-index: 1;
`

const SCommentContent = styled.div`
  padding: 16px 12px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    > ${SActionButtons} {
      display: flex !important;
      pointer-events: auto;
    }

    > ${SActionButtons} ${SMenuButton} {
      display: flex!important;
      pointer-events: auto;
    }

    > ${SActionButtons} [data-status-button] {
      display: flex !important;
      pointer-events: auto;
    }
  }
  
  &.active {
    > ${SActionButtons} {
      display: flex !important;
      pointer-events: auto;
    }

    > ${SActionButtons} ${SMenuButton} {
      display: flex!important;
      pointer-events: auto;
    }

    > ${SActionButtons} [data-status-button] {
      display: flex !important;
    }
  }

  &.highlighted {
    background: #303030;
  }

  &:hover {
      background: #303030;
    }

  &.active {
    background: #303030;
  }
`

const SComment = styled.div<{ active: boolean, id: string }>`
  position: relative;
`

const SCommentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  min-width: 0;
`

const SAvatar = styled.div`
  min-width: 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #FFFFFF40;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 14px;
`

const SAuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const SAuthorName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;    
  font-weight: 600;
  font-size: 14px;
  color: #FFFFFFD9;
`

const STimestamp = styled.span`
  flex: 0 0 auto;
  color: #FFFFFF73;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
`

const SContent = styled.p`
  color: #FFFFFF;
  margin: 0;
  line-height: 1.5;
  padding-left: 48px;
`

const SReplyBtnRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
`

const SReplyBtn = styled.p`
  margin-left: 48px;
  height: 20px;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #FFFFFFA6;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`

const SRepliesContainer = styled.div`
  position: relative;
`

const SRepliesToggle = styled.button`
  margin-left: 48px;
  background: none;
  border: none;
  color: #FFFFFFA6;
  font-weight: 400;
  cursor: pointer;
  font-size: 12px;
  height: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`

const SToggleIcon = styled.span<{ isVisible: boolean }>`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.isVisible ? '0deg' : '180deg'});
`

const SReplyWrapper = styled.div`
  margin-left: 36px;
  position: relative;
`

const SReplyFormRow = styled.div`
  padding-left: 44px;
  display: flex;
  margin-top: 16px;
`

const SCommentTime = styled.span`
  color: #FFFFFF73;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const SCommentTimeIcon = styled(ClockCircleOutlined)`
  font-size: 14px;
  color: #FFFFFF73;
`

const SAvatarLogo = styled.img`
  min-width: 32px;
  width: 32px;
  height: 32px;
  margin-right: 16px;
  border-radius: 50%;
`

const SAvatarImage = styled.img`
  min-width: 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 16px;
  object-fit: cover;
`

const SMessageWriter = styled(MessageWriter)`
  margin-top: 0;
`

const SEditRow = styled.div`
  display: flex;
  padding-top: 1px;
`

const SAttachmentsContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const SAttachmentImage = styled.img`
  margin-left: -2px;
  width: calc(100% + 4px);
  height: auto;
  max-height: 300px;
  max-width: 300px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
  transition: opacity 0.2s ease;
  border: 2px solid transparent;
  padding: 2px;

  &.active {
    border: 2px solid #AB7AE0;
    border-radius: 8px;
  }

  &:hover {
    opacity: 0.8;
  }
`

const SAnnotationLabel = styled.div`
  color: rgba(255, 255, 255, 0.45);
  display: flex;
  align-items: center;
  gap: 4px;
`

const SAnnotationLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const AnnotateSvgIcon = ({ fill }: { fill: string }) => (
  <svg width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      // eslint-disable-next-line max-len
      d="M14.1335 8.00033C14.1335 4.61298 11.3874 1.86686 8.00002 1.86686C4.61267 1.86686 1.86656 4.61298 1.86656 8.00033V14.1338H8.00002V15.3337H0.695984L0.675151 15.3252L0.666687 15.3044V8.00033C0.666687 3.95024 3.94993 0.666992 8.00002 0.666992C12.0501 0.666992 15.3334 3.95024 15.3334 8.00033C15.3334 12.0504 12.0501 15.3337 8.00002 15.3337V14.1338C11.3874 14.1338 14.1335 11.3877 14.1335 8.00033Z"
      fill={fill}
      fillOpacity="0.85"
    />
  </svg>
)
